// Smart Sash Admin Panel - Certification Settings Bar Component
// Configuration bar with checkboxes and number inputs for certification settings

import React from 'react';
import { CertificationConfiguration } from '@/models';
import { getInputBgClass } from '@/utils/certification';

export interface SettingsBarProps {
  isEditMode: boolean;
  settings: CertificationConfiguration;
  allQuestionsEnabled: boolean;
  numberOfQuestions: number;
  randomizeAnswers: boolean;
  onSettingsChange: (updates: Partial<CertificationConfiguration>) => void;
  onUISettingsChange: (field: string, value: boolean | number) => void;
}

export const SettingsBar: React.FC<SettingsBarProps> = ({
  isEditMode,
  settings,
  allQuestionsEnabled,
  numberOfQuestions,
  randomizeAnswers,
  onSettingsChange,
  onUISettingsChange,
}) => {
  const inputBgClass = getInputBgClass(isEditMode);

  return (
    <div className="d-flex flex-wrap gap-4 align-items-center">
      {/* Randomize Questions - maps to shuffleQuestions */}
      <div className="d-flex align-items-center gap-2">
        <div className="form-check mb-0">
          <input
            type="checkbox"
            className={`form-check-input ${inputBgClass}`}
            id="shuffleQuestions"
            checked={settings.shuffleQuestions}
            onChange={(e) => onSettingsChange({ shuffleQuestions: e.target.checked })}
            disabled={!isEditMode}
          />
          <label
            className="form-check-label small text-muted"
            htmlFor="shuffleQuestions"
          >
            Randomize Questions
          </label>
        </div>
      </div>

      {/* Randomize Answers - UI-only setting */}
      <div className="d-flex align-items-center gap-2">
        <div className="form-check mb-0">
          <input
            type="checkbox"
            className={`form-check-input ${inputBgClass}`}
            id="randomizeAnswers"
            checked={randomizeAnswers}
            onChange={(e) => onUISettingsChange('randomizeAnswers', e.target.checked)}
            disabled={!isEditMode}
          />
          <label
            className="form-check-label small text-muted"
            htmlFor="randomizeAnswers"
          >
            Randomize Answers
          </label>
        </div>
      </div>

      {/* All Questions - UI-only setting */}
      <div className="d-flex align-items-center gap-2">
        <div className="form-check mb-0">
          <input
            type="checkbox"
            className={`form-check-input ${inputBgClass}`}
            id="allQuestionsEnabled"
            checked={allQuestionsEnabled}
            onChange={(e) => onUISettingsChange('allQuestionsEnabled', e.target.checked)}
            disabled={!isEditMode}
          />
          <label
            className="form-check-label small text-muted"
            htmlFor="allQuestionsEnabled"
          >
            All Questions
          </label>
        </div>
      </div>

      {/* Number of Questions - disabled when All Questions is checked */}
      <div className="d-flex align-items-center gap-2">
        <label className="form-label mb-0 small text-muted" htmlFor="numberOfQuestions">
          # of Questions:
        </label>
        <input
          type="number"
          className={`form-control form-control-sm ${inputBgClass}`}
          id="numberOfQuestions"
          style={{ width: '80px' }}
          value={numberOfQuestions}
          onChange={(e) => onUISettingsChange('numberOfQuestions', parseInt(e.target.value) || 1)}
          disabled={!isEditMode || allQuestionsEnabled}
          min={1}
        />
      </div>

      {/* Percentage to Pass - maps to passingScore */}
      <div className="d-flex align-items-center gap-2">
        <label className="form-label mb-0 small text-muted" htmlFor="passingScore">
          % to Pass:
        </label>
        <input
          type="number"
          className={`form-control form-control-sm ${inputBgClass}`}
          id="passingScore"
          style={{ width: '80px' }}
          value={settings.passingScore}
          onChange={(e) => {
            const value = parseInt(e.target.value);
            if (value >= 0 && value <= 100) {
              onSettingsChange({ passingScore: value });
            }
          }}
          disabled={!isEditMode}
          min={0}
          max={100}
        />
      </div>

      {/* Show Correct Answers */}
      <div className="d-flex align-items-center gap-2">
        <div className="form-check mb-0">
          <input
            type="checkbox"
            className={`form-check-input ${inputBgClass}`}
            id="showCorrectAnswers"
            checked={settings.showCorrectAnswers}
            onChange={(e) => onSettingsChange({ showCorrectAnswers: e.target.checked })}
            disabled={!isEditMode}
          />
          <label
            className="form-check-label small text-muted"
            htmlFor="showCorrectAnswers"
          >
            Show Correct Answers
          </label>
        </div>
      </div>

      {/* Allow Retake */}
      <div className="d-flex align-items-center gap-2">
        <div className="form-check mb-0">
          <input
            type="checkbox"
            className={`form-check-input ${inputBgClass}`}
            id="allowRetake"
            checked={settings.allowRetake}
            onChange={(e) => onSettingsChange({ allowRetake: e.target.checked })}
            disabled={!isEditMode}
          />
          <label
            className="form-check-label small text-muted"
            htmlFor="allowRetake"
          >
            Allow Retake
          </label>
        </div>
      </div>
    </div>
  );
};

export default SettingsBar;
